import { useNavigate, useLocation } from 'react-router-dom';
import { Button, GoogleButton, Input, InputWrapperAuth } from '@/components/ui';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Lock, Mail } from 'lucide-react';
import { removeAdminData, setUserData } from '@/utils/cookies';
import {
	checkBenefitRedirection,
	clearPendingBenefitData,
	clearPublicSchedulingDraftFromLocalStorage,
	forwardAuthRedirectNavigateOptions,
	forwardRegisterFlowNavigateOptions,
	isAuthRedirectIntent,
} from '@/utils';
import { PLUMAA_SCHEDULING_RETURN_URL_KEY } from '@/constants/schedulingHandoff';
import { E2E_USER_AUTH } from '@/constants/e2eSchedulingTestIds';
import { redirectToOAuthLogin } from '@/utils/logoutBeforeLogin';
import {
	consumeMatchReturnPending,
	MATCH_PAGE_PATH,
} from '@/utils/matchHandoff';
import { useResumeSchedulingPayAfterAuth } from '@/hooks/useResumeSchedulingPayAfterAuth';
import { useLoginUser } from '@/services';
import { Helmet } from 'react-helmet-async';
import { Header } from '@/pages/public/landing/components/Header';

const schema = z.object({
	email: z.string().max(255, 'Email inválido').email('Email inválido'),
	password: z.string().min(1, 'A senha é obrigatória'),
});

type FormData = z.infer<typeof schema>;

const UserLogin = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { resumeSchedulingPayAfterAuth } = useResumeSchedulingPayAfterAuth();

	const handleLoginSuccess = async (data: Users.CreateResponse) => {
		setUserData(data.user);
		removeAdminData();

		// Veio do /match (respostas em rascunho): volta para enviar e ver o resultado.
		if (consumeMatchReturnPending()) {
			navigate(MATCH_PAGE_PATH, { replace: true });
			return;
		}

		const { shouldRedirect, path, state } = checkBenefitRedirection();

		if (shouldRedirect && path) {
			clearPendingBenefitData();
			navigate(path, { state });
			return;
		}

		const resumed = await resumeSchedulingPayAfterAuth(navigate);
		if (resumed) {
			try {
				sessionStorage.removeItem(PLUMAA_SCHEDULING_RETURN_URL_KEY);
			} catch {
				/* ignore */
			}
			return;
		}

		try {
			const schedulingReturn = sessionStorage.getItem(
				PLUMAA_SCHEDULING_RETURN_URL_KEY,
			);
			if (schedulingReturn) {
				sessionStorage.removeItem(PLUMAA_SCHEDULING_RETURN_URL_KEY);
				navigate(schedulingReturn);
				return;
			}
		} catch {
			/* ignore */
		}

		clearPublicSchedulingDraftFromLocalStorage();

		if (isAuthRedirectIntent(location.state)) {
			navigate('/formulario', {
				replace: true,
				...forwardAuthRedirectNavigateOptions(location.state),
			});
			return;
		}

		navigate('/formulario', {
			...forwardRegisterFlowNavigateOptions(location.state),
		});
	};

	const { mutate } = useLoginUser(handleLoginSuccess);

	const {
		register,
		handleSubmit,
		watch,
		formState: { errors },
	} = useForm<FormData>({
		resolver: zodResolver(schema),
	});

	const iconCommonClass = 'w-4 h-4 text-zinc-400';

	const onSubmit = (data: FormData) => mutate(data);

	return (
		<div className="flex min-h-screen flex-col overflow-y-auto bg-[#f8f9fa] font-sans">
			<Helmet>
				<title>Acesse sua conta | Plumaa</title>
				<meta
					name="description"
					content="Acesse sua conta Plumaa para gerenciar seus agendamentos e consultas."
				/>
			</Helmet>

			<Header />

			<main className="flex flex-1 items-center justify-center p-2">
				<div className="flex w-full max-w-[440px] flex-col gap-4 rounded-[2rem] bg-white p-5 shadow-sm md:p-8">
					<div className="space-y-0.5 text-center">
						<h1 className="text-xl font-black tracking-tight text-zinc-900 md:text-2xl">
							Olá de novo!
						</h1>
						<p className="text-[10px] font-medium text-zinc-400 md:text-xs">
							Por favor, insira suas informações
						</p>
					</div>

					<div className="w-full">
						<div className="flex w-full justify-center gap-1 rounded-lg bg-zinc-100 p-1">
							<button
								type="button"
								className="w-full cursor-pointer rounded-md border-none bg-transparent px-0 py-1.5 text-center text-xs font-bold text-zinc-500 focus:outline-none"
								onClick={() =>
									navigate('/cadastro', {
										...forwardRegisterFlowNavigateOptions(
											location.state,
										),
									})
								}
							>
								Cadastro
							</button>
							<button
								type="button"
								className="w-full cursor-pointer rounded-md border-none bg-white px-0 py-1.5 text-center text-xs font-bold text-zinc-900 shadow-sm focus:outline-none"
							>
								Entrar
							</button>
						</div>
					</div>

					<form
						onSubmit={handleSubmit(onSubmit)}
						className="space-y-4"
					>
						<div className="space-y-3">
							<div className="space-y-1">
								<label className="ml-1 text-[8px] font-black uppercase tracking-widest text-zinc-500">
									E-MAIL
								</label>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Mail className={iconCommonClass} />}
									error={errors.email?.message}
									value={watch('email')}
									displayError
								>
									<Input
										{...register('email')}
										data-testid={E2E_USER_AUTH.LOGIN_EMAIL}
										placeholder="nome@exemplo.com"
										className="h-9 border-zinc-200 text-xs focus:border-zinc-400"
									/>
								</InputWrapperAuth>
							</div>

							<div className="space-y-1">
								<div className="flex items-center justify-between px-1">
									<label className="text-[8px] font-black uppercase tracking-widest text-zinc-500">
										SENHA
									</label>
									{/* <button
										type="button"
										onClick={() => navigate('/recuperar-senha')}
										className="text-[9px] font-bold text-zinc-400 hover:text-zinc-600 transition-colors"
									>
										Esqueceu?
									</button> */}
								</div>
								<InputWrapperAuth
									className="max-w-none"
									icon={<Lock className={iconCommonClass} />}
									error={errors.password?.message}
									value={watch('password')}
									displayError
								>
									<Input
										type="password"
										{...register('password')}
										data-testid={
											E2E_USER_AUTH.LOGIN_PASSWORD
										}
										placeholder="********"
										className="h-9 border-zinc-200 text-xs focus:border-zinc-400"
									/>
								</InputWrapperAuth>
							</div>
						</div>

						<Button
							type="submit"
							data-testid={E2E_USER_AUTH.LOGIN_SUBMIT}
							className="w-full justify-center rounded-lg border-none bg-[#1a1a1a] py-3 text-sm font-bold text-white transition-all hover:bg-zinc-800"
							text="Entrar"
							endIcon={<ArrowRight size={14} className="ml-1" />}
						/>
					</form>

					<div className="space-y-4">
						<div className="relative flex items-center">
							<div className="flex-grow border-t border-zinc-100"></div>
							<span className="mx-4 flex-shrink text-[8px] font-black uppercase tracking-widest text-zinc-300">
								ou
							</span>
							<div className="flex-grow border-t border-zinc-100"></div>
						</div>

						<GoogleButton
							className="w-full max-w-none rounded-lg border-zinc-200 py-2.5 text-xs hover:bg-zinc-50"
							onClick={() => {
								const baseUrl =
									import.meta.env.VITE_BACKEND_URL.replace(
										/\/$/,
										'',
									);
								void redirectToOAuthLogin(
									`${baseUrl}/user/login/oauth`,
								);
							}}
						/>
					</div>

					<div className="border-t border-zinc-50 pt-2 text-center">
						<p className="text-[10px] font-medium text-zinc-400">
							Não tem uma conta?{' '}
							<button
								onClick={() =>
									navigate('/cadastro', {
										...forwardRegisterFlowNavigateOptions(
											location.state,
										),
									})
								}
								className="font-black text-zinc-900 underline-offset-4 hover:underline"
							>
								Cadastre-se gratuitamente
							</button>
						</p>
					</div>
				</div>
			</main>
		</div>
	);
};

export { UserLogin };
